/**
 * True when a SharePoint REST error message is the tenant genuinely saying a
 * list does not exist (404 / "does not exist" / "Invalid List Name") — as
 * opposed to a request that failed to reach the tenant at all (a dropped
 * connection, a DNS hiccup, a proxy timeout), which throws a generic
 * "Failed to fetch" that says nothing whatsoever about whether the list
 * exists.
 *
 * Conflating the two used to tell an operator to go re-provision a list that
 * was never actually missing, over what was really an ordinary network blip
 * — see Dispatch.tsx's scan-list banner, which gates the "provision it from
 * Migration Valve" message on this instead of on "any error at all". The
 * regex itself mirrors the ad-hoc check already used to detect a missing
 * list before creating one (GridDailyOutputService.ensureListExists and
 * siblings) — this just gives it a name so a caller reading an error after
 * the fact can ask the same question.
 */
export function isListMissingError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? "");
  return /404|does not exist|Invalid List Name/i.test(msg);
}

/**
 * True when SharePoint is refusing a COLUMN, not the list.
 *
 * Both failures say "does not exist", which is the trap: a list created
 * without its columns answers a `$select` naming them with "Field or property
 * 'x' does not exist", and `isListMissingError` matches that too. So a caller
 * must ask this FIRST, or a half-provisioned list is reported as absent and
 * the button offered to create it is refused for already existing — which is
 * exactly what LAZADA_Tokens did on 2026-09-21.
 */
export function isFieldMissingError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? "");
  return /Field or property/i.test(msg);
}

/**
 * True when SharePoint is refusing to create something that is already there.
 *
 * Provisioning treats it as success, so a run that half-finished can be
 * finished by pressing the same button again. Provisioning is a sequence of
 * remote writes; any step can fail, so every step has to be safe to repeat.
 */
export function isAlreadyExistsError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? "");
  return /already exists/i.test(msg);
}
